import React from "react";
import { Table, Card, CardContent, Typography, Avatar, Box } from "@material-ui/core";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import { red } from "@material-ui/core/colors";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: 16,
    overflow: "hidden",
    border: "1px solid rgba(0,0,0,0.06)",
    boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
    background: "#fff",
  },
  header: {
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    background: "#fff",
    borderBottom: "1px solid rgba(0,0,0,0.06)",
  },
  avatar: {
    backgroundColor: red[600],
    width: 44,
    height: 44,
    borderRadius: 12,
    fontWeight: 800,
    letterSpacing: 0.5,
    boxShadow: "0 4px 10px rgba(211,47,47,0.18)",
  },
  title: {
    fontWeight: 800,
    lineHeight: 1.1,
  },
  subtitle: {
    marginTop: 2,
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
  content: {
    padding: theme.spacing(2),
  },
  table: {
    width: "100%",
    tableLayout: "fixed",
    borderCollapse: "separate",
    borderSpacing: 0,
  },
  row: {
    "&:not(:last-child)": {
      boxShadow: "inset 0 -1px 0 rgba(0,0,0,0.06)",
    },
  },
  th: {
    width: 160,
    color: theme.palette.text.secondary,
    fontSize: 13,
    fontWeight: 700,
    padding: theme.spacing(1.25, 0),
    borderBottom: "none",
    verticalAlign: "top",
  },
  tdCell: {
    padding: theme.spacing(1.25, 0),
    borderBottom: "none",
    verticalAlign: "top",
  },
  value: {
    display: "block",
    width: "100%",
    fontSize: 13,
    fontWeight: 600,
    color: theme.palette.text.primary,
    lineHeight: 1.6,
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
  valueJustify: {
    textAlign: "justify",
    textAlignLast: "left",
  },
}));

const normalize = (v) => String(v ?? "").trim();

const isNoRegistra = (v) => {
  const s = normalize(v).toUpperCase();
  return s === "" || s === "NO REGISTRA" || s === "NO REGISTRA.";
};

const hasMeaningfulText = (v) => !isNoRegistra(v);

function Value({ text, justify = false }) {
  const classes = useStyles();
  const value = hasMeaningfulText(text) ? normalize(text) : "NO REGISTRA";

  return (
    <span className={`${classes.value} ${justify ? classes.valueJustify : ""}`}>
      {value}
    </span>
  );
}

function CardHeaderModern({ code, title, subtitle }) {
  const classes = useStyles();

  return (
    <div className={classes.header}>
      <Avatar className={classes.avatar} variant="square">
        {code}
      </Avatar>
      <Box>
        <Typography className={classes.title} variant="subtitle1">
          {title}
        </Typography>
        {subtitle && (
          <Typography className={classes.subtitle}>{subtitle}</Typography>
        )}
      </Box>
    </div>
  );
}

export function InfoBasicaAsignaturaCard({ data }) {
  const classes = useStyles();
  if (!data) {
    return (
      <Card className={classes.card}>
        <CardHeaderModern
          code="IN"
          title="Información básica"
          subtitle="Cargando datos"
        />
        <CardContent className={classes.content}>
          <Typography variant="body2" color="textSecondary">
            Cargando información de la asignatura...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={classes.card}>
      <CardHeaderModern
        code="IN"
        title="Información básica"
        subtitle="Plan de asignatura"
      />
      <CardContent className={classes.content}>
        <Table className={classes.table}>
          <TableBody>
            <TableRow className={classes.row}>
              <TableCell className={classes.th}>Asignatura</TableCell>
              <TableCell className={classes.tdCell} align="right">
                <Value text={data.asignatura_nombre} />
              </TableCell>
            </TableRow>
            <TableRow className={classes.row}>
              <TableCell className={classes.th}>Código</TableCell>
              <TableCell className={classes.tdCell} align="right">
                <Value text={data.codigo_asignatura} />
              </TableCell>
            </TableRow>
            <TableRow className={classes.row}>
              <TableCell className={classes.th}>Departamento</TableCell>
              <TableCell className={classes.tdCell} align="right">
                <Value text={data.departamento_nombre} />
              </TableCell>
            </TableRow>
            <TableRow className={classes.row}>
              <TableCell className={classes.th}>Programa</TableCell>
              <TableCell className={classes.tdCell} align="right">
                <Value text={data.programa_nombre} />
              </TableCell>
            </TableRow>
            <TableRow className={classes.row}>
              <TableCell className={classes.th}>Código del Programa</TableCell>
              <TableCell className={classes.tdCell} align="right">
                <Value text={data.codigo_programa} />
              </TableCell>
            </TableRow>
            <TableRow className={classes.row}>
              <TableCell className={classes.th}>Plan de estudio</TableCell>
              <TableCell className={classes.tdCell} align="right">
                <Value text={data.plan_syll?.nombre} />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export default function SisefTables({ plan }) {
  const classes = useStyles();
  const item = plan?.[0] ?? {};
  const valoresTieneTexto = hasMeaningfulText(item.valores_plan);

  return (
    <Card className={classes.card}>
      <CardHeaderModern
        code="IN"
        title="Información básica"
        subtitle="Plan de asignatura"
      />
      <CardContent className={classes.content}>
        <Table className={classes.table}>
          <TableBody>
            <TableRow className={classes.row}>
              <TableCell className={classes.th}>Asignatura</TableCell>
              <TableCell className={classes.tdCell} align="right">
                <Value text={item.asignatura_nombre} />
              </TableCell>
            </TableRow>
            <TableRow className={classes.row}>
              <TableCell className={classes.th}>Periodo</TableCell>
              <TableCell className={classes.tdCell} align="right">
                <Value text={item.periodo} />
              </TableCell>
            </TableRow>
            <TableRow className={classes.row}>
              <TableCell className={classes.th}>Plan de estudio</TableCell>
              <TableCell className={classes.tdCell} align="right">
                <Value text={item.plan_syll?.nombre} />
              </TableCell>
            </TableRow>
            <TableRow className={classes.row}>
              <TableCell className={classes.th}>Valores</TableCell>
              <TableCell
                className={classes.tdCell}
                align={valoresTieneTexto ? "left" : "right"}
              >
                <Value text={item.valores_plan} justify={valoresTieneTexto} />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export function RubricasTables({ nombre }) {
  const classes = useStyles();
  return (
    <Card className={classes.card}>
      <CardHeaderModern
        code="RD"
        title="Información básica"
        subtitle="Rúbrica"
      />
      <CardContent className={classes.content}>
        <Table className={classes.table}>
          <TableBody>
            <TableRow className={classes.row}>
              <TableCell className={classes.th}>Asignatura</TableCell>
              <TableCell className={classes.tdCell} align="right">
                <Value text={nombre} />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}